import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import CrudForm from "../../components/dashboard/form/CrudForm";
import { useCrud } from "../../hooks/useCrud";
import Swal from "sweetalert2";

const generateSlug = (text) =>
  text.toString().toLowerCase().trim().replace(/\s+/g, "-");

export default function Category() {
  const navigate = useNavigate();
const [searchText, setSearchText] = useState("");
  const {
    data: categories,
    loading,
    errors,
    createItem,
    updateItem,
    deleteItem,
  } = useCrud(`${process.env.REACT_APP_API_URL}/backend/categories`, [
    "cat_image",
  ]);

  const [editingItem, setEditingItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // ✅ API BASED FIELDS
  const allowedFields = useMemo(
    () => [
      "name",
      "cat_image",
      "status",
      "seo_title",
      "seo_description",
      "seo_keywords",
      "description",
    ],
    []
  );

  const getFormData = useCallback(
    (item) => {
      if (!item)
        return {
          name: "",
          cat_image: null,
          status: 1,
          seo_title: "",
          seo_description: "",
          seo_keywords: "",
          description: "",
        };

      return allowedFields.reduce((acc, field) => {
        acc[field] =
          field === "status"
            ? Number(item[field] ?? 1)
            : field === "cat_image"
              ? item[field] || null
              : item[field] || "";
        return acc;
      }, {});
    },
    [allowedFields]
  );

  const hasDuplicate = useCallback(
    (name, slug, id = null) =>
      categories.some(
        (cat) =>
          (cat.name.toLowerCase() === name.toLowerCase() ||
            cat.slug.toLowerCase() === slug.toLowerCase()) &&
          cat.id !== id
      ),
    [categories]
  );

 const showSwal = useCallback((title, text, icon) => {
  Swal.fire({
    title,
    text,
    icon,
    confirmButtonText: "OK",
    confirmButtonColor: "#214b7a", // 🔵 custom blue
  });
}, []);


const handleSubmit = useCallback(
  async (formData) => {
    const name = String(formData.name || "").trim();

    if (!name) {
      showSwal(
        "Error!",
        "Category name is required.",
        "warning"
      );
      return;
    }

    const slug = generateSlug(name);

    if (hasDuplicate(name, slug, editingItem?.id)) {
      showSwal(
        "Duplicate Found!",
        "A category with the same name already exists.",
        "warning"
      );
      return;
    }

    const dataToSend = {
      name,
      slug,
      status: Number(formData.status ?? 1),
      seo_title: formData.seo_title ?? "",
      seo_description: formData.seo_description ?? "",
      seo_keywords: formData.seo_keywords ?? "",
      description: formData.description ?? "",
    };

    // Include the image only if a new file was selected.
    if (formData.cat_image instanceof File) {
      dataToSend.cat_image = formData.cat_image;
    }

    try {
      if (editingItem) {
        console.log("Updating category ID:", editingItem.id);
        console.log("Update payload:", dataToSend);

        await updateItem(editingItem.id, dataToSend);

        showSwal(
          "Updated!",
          "Category updated successfully.",
          "success"
        );
      } else {
        await createItem(dataToSend);

        showSwal(
          "Created!",
          "Category created successfully.",
          "success"
        );
      }

      setModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      console.error(
        "Category save error:",
        error.response?.data || error
      );

      showSwal(
        "Error!",
        error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          "Something went wrong.",
        "error"
      );
    }
  },
  [
    editingItem,
    hasDuplicate,
    updateItem,
    createItem,
    showSwal,
  ]
);


  const openModal = useCallback((item = null) => {
    setEditingItem(item);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingItem(null);
  }, []);

const handleDelete = useCallback(
  (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#214b7a", // 👈 change color here
      cancelButtonColor: "#6c757d",
      buttonsStyling: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteItem(id);
          showSwal("Deleted!", "Category deleted.", "success");
        } catch (error) {
          Swal.fire("Error!", error.message || "Delete failed.", "error");
        }
      }
    });
  },
  [deleteItem]
);

  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        const updatedData = {
          name: item.name,
          slug: item.slug,
          status: item.status ? 0 : 1,
        };

        await updateItem(item.id, updatedData);
        showSwal("Success!", "Status updated.", "success");
      } catch {
        showSwal("Error!", "Failed to update status.", "error");
      }
    },
    [updateItem, showSwal]
  );

  const tableData = useMemo(
    () => categories.map((item, idx) => ({ ...item, sl: idx + 1 })),
    [categories]
  );

  return (
    <DashboardWrapper>
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Back
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => openModal()} 
                     style={{
    backgroundColor: "#214b7a",
    borderColor: "#214b7a",
    color: "#fff"
  }}
          >
            Create Category
          </button>
        </div>

        <div className="card shadow">
                 <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
       <h4 className="mb-0">Categories</h4>

  <input
    type="text"
    className="form-control"
    placeholder="Search products..."
    value={searchText}
    onChange={(e) => setSearchText(e.target.value)}
    style={{
      width: "260px",
      maxWidth: "100%",
      borderRadius: "6px",
    }}
  />
</div>

          {modalOpen && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-dialog-centered ">
                <div className="modal-content">

                  <div className="modal-header">
                    <h5 className="modal-title px-5">
                      {editingItem ? "Edit Category" : "Create Category"}
                    </h5>

                    <button
                      className="btn-close"
                      onClick={closeModal}
                    />
                  </div>

                  <div className="modal-body px-5 py-4">
                    <CrudForm
                      initialData={getFormData(editingItem)}
                      onSubmit={handleSubmit}
                      allowedFields={allowedFields}
                      fileFields={[
                        { name: "cat_image", multiple: false }
                      ]}
                    />
                  </div>

                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" />
            </div>
          ) : (
            <div className="card-body">
              <CrudTable
                data={tableData}
                onEdit={openModal}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                   searchText={searchText}
                columns={["sl", "id", "cat_image", "name", "status"]}
              />
            </div>
          )}
        </div>
      </div>
    </DashboardWrapper>
  );
}